import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useRef, useState } from 'react';
import { captureVideoFrameAsDataUrl, isVideoFrameReady, stopMediaStream } from '@/lib/capture';

type Props = {
  /** Recebe o quadro em data URL JPEG. LGPD: não guardar em state/storage. */
  onCapture: (base64Image: string) => void;
  disabled?: boolean;
  hint?: string;
};

type CameraState = 'starting' | 'ready' | 'denied' | 'unavailable';

/** Captura simples pela câmera frontal (reconhecimento e modo mock da selfie). */
export function CameraCapture({ onCapture, disabled = false, hint = 'Centralize o rosto e toque para capturar' }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [state, setState] = useState<CameraState>('starting');

  useEffect(() => {
    let disposed = false;
    let stream: MediaStream | null = null;

    const open = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setState('unavailable');
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        });
        if (disposed) {
          stopMediaStream(stream);
          return;
        }
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          void video.play().catch(() => undefined);
        }
      } catch (error) {
        const name = error instanceof Error ? error.name : '';
        setState(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable');
      }
    };

    void open();
    return () => {
      disposed = true;
      if (stream) stopMediaStream(stream);
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, []);

  const handleCapture = useCallback(() => {
    const video = videoRef.current;
    if (!video || !isVideoFrameReady(video)) return;
    onCapture(captureVideoFrameAsDataUrl(video));
  }, [onCapture]);

  if (state === 'denied' || state === 'unavailable') {
    return (
      <Box className="result-card">
        <Typography variant="h2">Câmera indisponível</Typography>
        <Typography color="text.secondary">
          {state === 'denied'
            ? 'A permissão de câmera foi negada. Libere o acesso nas configurações do navegador e recarregue a página.'
            : 'Não encontramos uma câmera neste aparelho ou o navegador não permite o acesso (é preciso HTTPS ou localhost).'}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box className="camera">
        <video
          ref={videoRef}
          className="camera__video"
          autoPlay
          playsInline
          muted
          onCanPlay={() => setState('ready')}
        />
        <Box className="camera__guide" aria-hidden />
        {state === 'starting' && <Box className="camera__status">Abrindo a câmera…</Box>}
      </Box>
      <Box className="shutter">
        <button
          type="button"
          className="shutter__button"
          aria-label="Capturar"
          onClick={handleCapture}
          disabled={disabled || state !== 'ready'}
        >
          <span className="shutter__inner" aria-hidden />
        </button>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {hint}
        </Typography>
      </Box>
    </>
  );
}
